// Constants
import { DefaultOrderHint } from "@ms/uno-constants/lib/local/OrderHintConstants";
import cloneDeep from "lodash/cloneDeep";
import { CustomFieldChoiceExtensionKeys } from "../../../service/planner/extensions/ICustomFieldChoiceExtensionsResource";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
/**
 * The custom field choice entity implementation.
 */ export class CustomFieldChoice extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed) {
        return {
            displayName: changed.displayName,
            value: changed.value,
            extensions: changed.extensions && this.serializeExtensionsDiff(changed.extensions)
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            extensions: (value)=>CustomFieldChoice.deserializeExtensions(value)
        });
    }
    static fromResource(resource) {
        return new CustomFieldChoice({
            id: resource.id,
            version: resource["@etag"],
            customFieldId: resource.customFieldId,
            displayName: resource.displayName,
            value: resource.value,
            extensions: this.deserializeExtensions(resource.extensions)
        });
    }
    static deserializeExtensions(extensions) {
        const output = {
            orderHint: DefaultOrderHint
        };
        // Read color data from the colorData extension key
        // Value is a stringified JSON object: "{\"backgroundColor\": \"#d8cce7\", \"foregroundColor\": \"#401b6c\"}"
        const serializedColorData = extensions[CustomFieldChoiceExtensionKeys.colorData];
        if (serializedColorData !== undefined) {
            if (serializedColorData === null) {
                output.colorData = null;
            } else {
                try {
                    const colorData = JSON.parse(serializedColorData);
                    if (colorData.backgroundColor !== undefined && colorData.foregroundColor !== undefined) {
                        output.colorData = {
                            backgroundColor: colorData.backgroundColor,
                            foregroundColor: colorData.foregroundColor
                        };
                    }
                } catch  {
                // Ignore malformed colorData extension data
                }
            }
        }
        // Read order hint from its own top-level extension key
        // Value is the raw order hint string (e.g. "8586143420439:")
        const serializedOrderHint = extensions[CustomFieldChoiceExtensionKeys.orderHint];
        if (serializedOrderHint != null) {
            output.orderHint = serializedOrderHint;
        }
        return output;
    }
    getClone() {
        return new CustomFieldChoice({
            id: this.id,
            version: this.version,
            customFieldId: this.customFieldId,
            displayName: this.displayName,
            value: this.value,
            extensions: cloneDeep(this.extensions)
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            customFieldId: this.customFieldId,
            displayName: this.displayName,
            value: this.value,
            extensions: this.serializeExtensions()
        };
    }
    forCreate() {
        return {
            customFieldId: this.customFieldId,
            displayName: this.displayName,
            value: this.value,
            extensions: this.serializeExtensions()
        };
    }
    /**
     * Sets the choice color and returns a new instance.
     * @param colorData The color data with backgroundColor and foregroundColor, or `null` to clear colors
     * @returns A new ICustomFieldChoice instance with the updated colors
     */ setChoiceColor(colorData) {
        return this.setProperty("extensions", {
            ...this.extensions,
            colorData
        });
    }
    /**
     * Sets the order hint for the choice and returns a new instance.
     * @param orderHint The order hint string value
     * @returns A new ICustomFieldChoice instance with the updated order hint
     */ setOrderHint(orderHint) {
        return this.setProperty("extensions", {
            ...this.extensions,
            orderHint
        });
    }
    /**
     * Serializes only the changed extension fields for use in PATCH requests.
     * @param diff The diff containing only the changed extension properties
     */ serializeExtensionsDiff(diff) {
        const output = {};
        if (diff.colorData !== undefined) {
            if (this.extensions.colorData) {
                const resource = {
                    backgroundColor: this.extensions.colorData.backgroundColor,
                    foregroundColor: this.extensions.colorData.foregroundColor
                };
                output[CustomFieldChoiceExtensionKeys.colorData] = JSON.stringify(resource);
            } else {
                output[CustomFieldChoiceExtensionKeys.colorData] = null;
            }
        }
        if (diff.orderHint !== undefined) {
            output[CustomFieldChoiceExtensionKeys.orderHint] = this.extensions.orderHint;
        }
        return output;
    }
    serializeExtensions() {
        const output = {};
        if (this.extensions.colorData != null) {
            const colorData = {
                backgroundColor: this.extensions.colorData.backgroundColor,
                foregroundColor: this.extensions.colorData.foregroundColor
            };
            output[CustomFieldChoiceExtensionKeys.colorData] = JSON.stringify(colorData);
        }
        if (this.extensions.orderHint !== undefined) {
            output[CustomFieldChoiceExtensionKeys.orderHint] = this.extensions.orderHint;
        }
        return output;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.customFieldId = propertyBag.customFieldId;
        this.displayName = propertyBag.displayName;
        this.value = propertyBag.value;
        this.extensions = propertyBag.extensions ?? {
            orderHint: DefaultOrderHint
        };
    }
}
